import { useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import useFetch from '../hooks/useFetch';
import type { FetchError } from '../hooks/useFetch';
import { TemplateLoader } from '../template/TemplateLoader';
import { SUPPORTED_CONTRACT_VERSIONS } from '../template/contract';
import type { NotFoundPayload, ViewPayload, VcardViewPayload } from '../template/contract';
import { slugify } from '../utils/slug';
import Loading from './Loading';
import { setMapTileUrl } from '../template/mapTiles';

// Aktuelle Kontrakt-Version aus der Konstante ableiten statt eine Zahl
// hartzucodieren (steht inzwischen auf 3, für die Auswahl der Ansicht ohnehin
// unerheblich).
const CONTRACT_VERSION = SUPPORTED_CONTRACT_VERSIONS.at(-1)!;

function EmployeeData() {
    const { urlKey } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { request, data, error } = useFetch<VcardViewPayload>();

    // Kanonischer Slug (lowercase). Weicht der aufgerufene Key davon ab
    // (z. B. alter/gedruckter Link `/Saskia_schwarzenberger`), leiten wir auf
    // die kanonische URL um (gleicher Host, Query-Params bleiben erhalten).
    const canonical = urlKey ? slugify(urlKey) : '';
    const needsRedirect = !!urlKey && !!canonical && canonical !== urlKey;

    useEffect(() => {
        if (needsRedirect) {
            const search = searchParams.toString();
            navigate(`/${canonical}${search ? `?${search}` : ''}`, { replace: true });
        }
    }, [needsRedirect, canonical, searchParams, navigate]);

    useEffect(() => {
        if (urlKey && !needsRedirect) {
            const preview = searchParams.get('preview');
            const previewQuery = preview ? `&preview=${encodeURIComponent(preview)}` : '';
            request(`vcard/context?key=${urlKey}${previewQuery}`);
        }
    }, [urlKey, needsRedirect, request, searchParams]);

    // Kachel-URL der Karte aus der Serverantwort uebernehmen, BEVOR ein Template
    // rendert. Sie traegt den Schluessel der Marke und steht bewusst neben dem
    // Kontrakt-Kontext: so bleibt jedes Marken-Template schluesselfrei
    // (src/template/mapTiles.ts erklaert das Warum).
    useEffect(() => {
        setMapTileUrl((data as { mapTileUrl?: string } | undefined)?.mapTileUrl);
    }, [data]);

    // 404/410 werden INLINE gerendert, die Adresse bleibt in der Adresszeile
    // (kein navigate) — ein Besucher, der einen QR-Code scannt, muss die
    // aufgerufene Adresse sehen können, um einen Tippfehler zu erkennen.
    // Ausserdem machte eine Umleitung aus der serverseitigen 404/410-Antwort
    // (T-28) wieder eine 200-Antwort auf einer anderen Adresse.
    if (error) {
        const err = error as FetchError;
        const body = err.body as ViewPayload | undefined;
        if ((err.status === 404 || err.status === 410) && body?.kind) {
            return <TemplateLoader payload={body} view={body.kind} />;
        }
        const fallback: NotFoundPayload = {
            kind: 'notFound',
            contractVersion: CONTRACT_VERSION,
            context: { brand: {} },
            template: null,
        };
        return <TemplateLoader payload={fallback} view="notFound" />;
    }
    if (needsRedirect || !data) return <Loading/>

    return <TemplateLoader payload={data} urlKey={canonical} />;
}

export default EmployeeData;
